import { useEffect } from 'react';
import { useStore } from 'react-redux';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { departmentsApi } from '@/store/query/departments';
import { FILTER_OPTIONS_STALE_SECONDS } from '@/constants/app-constants';
import type { DepartmentNode } from '@/components/formBuilder/SelectComponents/departmentPath';

const EMPTY_DEPARTMENTS: DepartmentNode[] = [];

// Stores that already hold the shared subscription below.
const subscribedStores = new WeakSet<object>();

/**
 * The account's departments for showing a department's hierarchy, read from
 * the shared query cache (the same entry the department filters and selects
 * use).
 *
 * A department path renders in table cells, and many tables remount their
 * cells on every render. A query hook per cell would dispatch on each mount
 * and unmount, and a host that re-renders on store updates would then remount
 * the cells again, in a loop. So the cells only read the cache, and one
 * subscription per store, opened by the first path to mount, keeps it loaded.
 */
export const useDepartmentsList = (): DepartmentNode[] => {
  const store = useStore();
  const { data } =
    departmentsApi.endpoints.getAtsDepartments.useQueryState(undefined);

  useEffect(() => {
    if (subscribedStores.has(store)) return;
    subscribedStores.add(store);
    // The app store runs the thunk middleware; useStore types it for plain actions.
    const dispatch = store.dispatch as ThunkDispatch<
      unknown,
      unknown,
      AnyAction
    >;
    dispatch(
      departmentsApi.endpoints.getAtsDepartments.initiate(undefined, {
        forceRefetch: FILTER_OPTIONS_STALE_SECONDS,
      })
    );
  }, [store]);

  return data ?? EMPTY_DEPARTMENTS;
};
